import { useTraderText } from '../context/TraderLanguageContext';
import { useBinanceHistory } from '../hooks/useBinanceHistory';
import {useAccountPriceHistory} from '../hooks/useAccountPriceHistory';
import {AccountPriceNotice} from './AccountPriceNotice';
import { isBinancePair } from '../utils/binance';
import { BinanceStatus } from './BinanceStatus';
import { useTraderLanguage } from '../context/TraderLanguageContext';
/**
 * BrokerChart - Lightweight Charts v4
 * With extensive indicators support (TradingView-like)
 */
import { useEffect, useRef, useState, useCallback, memo, useMemo } from 'react';
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts';
import { chartLocale } from '../utils/locale';
import { useTraderTheme } from '../context/TraderThemeContext';
import { Activity, ChevronDown, X, Layers, TrendingUp, TrendingDown, Lock, Clock, BarChart3, CandlestickChart, Maximize2 } from 'lucide-react';

// ============= INDICATOR CALCULATION FUNCTIONS =============

// Simple Moving Average
const calculateSMA = (data, period) => {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) continue;
    let sum = 0;
    for (let j = 0; j < period; j++) {
      sum += data[i - j].close;
    }
    result.push({ time: data[i].time, value: sum / period });
  }
  return result;
};

// Exponential Moving Average
const calculateEMA = (data, period) => {
  const result = [];
  const multiplier = 2 / (period + 1);
  let ema = 0;
  
  for (let i = 0; i < data.length; i++) {
    if (i === 0) {
      ema = data[i].close;
    } else {
      ema = (data[i].close - ema) * multiplier + ema;
    }
    if (i >= period - 1) {
      result.push({ time: data[i].time, value: ema });
    }
  }
  return result;
};

// Bollinger Bands
const calculateBollingerBands = (data, period = 20, multiplier = 2) => {
  const sma = calculateSMA(data, period);
  const upper = [];
  const lower = [];
  
  for (let i = period - 1; i < data.length; i++) {
    let sumSquares = 0;
    for (let j = 0; j < period; j++) {
      sumSquares += Math.pow(data[i - j].close - sma[i - period + 1].value, 2);
    }
    const stdDev = Math.sqrt(sumSquares / period);
    const smaValue = sma[i - period + 1].value;
    upper.push({ time: data[i].time, value: smaValue + stdDev * multiplier });
    lower.push({ time: data[i].time, value: smaValue - stdDev * multiplier });
  }
  
  return { middle: sma, upper, lower };
};

// RSI (Relative Strength Index)
const calculateRSI = (data, period = 14) => {
  const result = [];
  const gains = [];
  const losses = [];
  
  for (let i = 1; i < data.length; i++) {
    const change = data[i].close - data[i - 1].close;
    gains.push(change > 0 ? change : 0);
    losses.push(change < 0 ? Math.abs(change) : 0);
  }
  
  for (let i = period; i < gains.length + 1; i++) {
    let avgGain = 0;
    let avgLoss = 0;
    
    if (i === period) {
      for (let j = 0; j < period; j++) {
        avgGain += gains[i - period + j];
        avgLoss += losses[i - period + j];
      }
      avgGain /= period;
      avgLoss /= period;
    } else {
      avgGain = (gains[i - 1] + (period - 1) * result[result.length - 1]?.avgGain) / period;
      avgLoss = (losses[i - 1] + (period - 1) * result[result.length - 1]?.avgLoss) / period;
    }
    
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    const rsi = 100 - (100 / (1 + rs));
    
    result.push({ 
      time: data[i].time, 
      value: rsi,
      avgGain,
      avgLoss
    });
  }
  
  return result.map(r => ({ time: r.time, value: r.value }));
};

// MACD (Moving Average Convergence Divergence)
const calculateMACD = (data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) => {
  const fastEMA = calculateEMA(data, fastPeriod);
  const slowEMA = calculateEMA(data, slowPeriod);
  
  const macdLine = [];
  const startIdx = slowPeriod - 1;
  
  for (let i = 0; i < slowEMA.length; i++) {
    const fastValue = fastEMA[i + (slowPeriod - fastPeriod)]?.value;
    const slowValue = slowEMA[i]?.value;
    if (fastValue !== undefined && slowValue !== undefined) {
      macdLine.push({
        time: slowEMA[i].time,
        value: fastValue - slowValue
      });
    }
  }
  
  // Signal line (EMA of MACD)
  const signalLine = [];
  const signalMultiplier = 2 / (signalPeriod + 1);
  let signalEma = macdLine[0]?.value || 0;
  
  for (let i = 0; i < macdLine.length; i++) {
    if (i === 0) {
      signalEma = macdLine[i].value;
    } else {
      signalEma = (macdLine[i].value - signalEma) * signalMultiplier + signalEma;
    }
    if (i >= signalPeriod - 1) {
      signalLine.push({ time: macdLine[i].time, value: signalEma });
    }
  }
  
  // Histogram
  const histogram = [];
  const histogramStart = signalPeriod - 1;
  for (let i = histogramStart; i < macdLine.length; i++) {
    const macdValue = macdLine[i].value;
    const signalValue = signalLine[i - histogramStart]?.value;
    if (signalValue !== undefined) {
      histogram.push({
        time: macdLine[i].time,
        value: macdValue - signalValue,
        color: macdValue - signalValue >= 0 ? '#26a69a' : '#ef5350'
      });
    }
  }
  
  return { macdLine, signalLine, histogram };
};

// Stochastic Oscillator
const calculateStochastic = (data, kPeriod = 14, dPeriod = 3) => {
  const kLine = [];
  const dLine = [];
  
  for (let i = kPeriod - 1; i < data.length; i++) {
    let highestHigh = -Infinity;
    let lowestLow = Infinity;
    
    for (let j = 0; j < kPeriod; j++) {
      const candle = data[i - j];
      highestHigh = Math.max(highestHigh, candle.high);
      lowestLow = Math.min(lowestLow, candle.low);
    }
    
    const k = highestHigh === lowestLow ? 50 : 
      ((data[i].close - lowestLow) / (highestHigh - lowestLow)) * 100;
    
    kLine.push({ time: data[i].time, value: k });
  }
  
  // %D line (SMA of %K)
  for (let i = dPeriod - 1; i < kLine.length; i++) {
    let sum = 0;
    for (let j = 0; j < dPeriod; j++) {
      sum += kLine[i - j].value;
    }
    dLine.push({ time: kLine[i].time, value: sum / dPeriod });
  }
  
  return { kLine, dLine };
};

// ATR (Average True Range)
const calculateATR = (data, period = 14) => {
  const result = [];
  const trueRanges = [];
  
  for (let i = 1; i < data.length; i++) {
    const high = data[i].high;
    const low = data[i].low;
    const prevClose = data[i - 1].close;
    
    const tr = Math.max(
      high - low,
      Math.abs(high - prevClose),
      Math.abs(low - prevClose)
    );
    trueRanges.push(tr);
  }
  
  for (let i = period - 1; i < trueRanges.length; i++) {
    let sum = 0;
    for (let j = 0; j < period; j++) {
      sum += trueRanges[i - j];
    }
    result.push({ time: data[i + 1].time, value: sum / period });
  }
  
  return result;
};

// Williams %R
const calculateWilliamsR = (data, period = 14) => {
  const result = [];
  
  for (let i = period - 1; i < data.length; i++) {
    let highestHigh = -Infinity;
    let lowestLow = Infinity;
    
    for (let j = 0; j < period; j++) {
      const candle = data[i - j];
      highestHigh = Math.max(highestHigh, candle.high);
      lowestLow = Math.min(lowestLow, candle.low);
    }
    
    const wr = highestHigh === lowestLow ? -50 :
      ((highestHigh - data[i].close) / (highestHigh - lowestLow)) * -100;
    
    result.push({ time: data[i].time, value: wr });
  }
  
  return result;
};

// CCI (Commodity Channel Index)
const calculateCCI = (data, period = 20) => {
  const result = [];
  const typicalPrices = data.map(d => (d.high + d.low + d.close) / 3);
  
  for (let i = period - 1; i < data.length; i++) {
    // SMA of typical price
    let smaSum = 0;
    for (let j = 0; j < period; j++) {
      smaSum += typicalPrices[i - j];
    }
    const sma = smaSum / period;
    
    // Mean deviation
    let mdSum = 0;
    for (let j = 0; j < period; j++) {
      mdSum += Math.abs(typicalPrices[i - j] - sma);
    }
    const md = mdSum / period;
    
    const cci = md === 0 ? 0 : (typicalPrices[i] - sma) / (0.015 * md);
    result.push({ time: data[i].time, value: cci });
  }
  
  return result;
};

// Volume
const calculateVolume = (data) => {
  return data.map(candle => ({
    time: candle.time,
    value: candle.market_source === 'binance' ? candle.volume : candle.volume || Math.random() * 1000000 + 500000,
    color: candle.close >= candle.open ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)'
  }));
};

// VWAP (Volume Weighted Average Price)
const calculateVWAP = (data) => {
  const result = [];
  let cumulativeTPV = 0;
  let cumulativeVolume = 0;
  
  for (let i = 0; i < data.length; i++) {
    const typicalPrice = (data[i].high + data[i].low + data[i].close) / 3;
    const volume = data[i].volume || 1000000;
    
    cumulativeTPV += typicalPrice * volume;
    cumulativeVolume += volume;
    
    result.push({
      time: data[i].time,
      value: cumulativeTPV / cumulativeVolume
    });
  }
  
  return result;
};

// Parabolic SAR
const calculateParabolicSAR = (data, af = 0.02, maxAf = 0.2) => {
  const result = [];
  if (data.length < 2) return result;
  
  let isUpTrend = data[1].close > data[0].close;
  let sar = isUpTrend ? data[0].low : data[0].high;
  let ep = isUpTrend ? data[1].high : data[1].low;
  let currentAf = af;
  
  for (let i = 1; i < data.length; i++) {
    const prevSar = sar;
    sar = prevSar + currentAf * (ep - prevSar);
    
    if (isUpTrend) {
      sar = Math.min(sar, data[i - 1].low, i > 1 ? data[i - 2].low : data[i - 1].low);
      if (data[i].low < sar) {
        isUpTrend = false;
        sar = ep;
        ep = data[i].low;
        currentAf = af;
      } else {
        if (data[i].high > ep) {
          ep = data[i].high;
          currentAf = Math.min(currentAf + af, maxAf);
        }
      }
    } else {
      sar = Math.max(sar, data[i - 1].high, i > 1 ? data[i - 2].high : data[i - 1].high);
      if (data[i].high > sar) {
        isUpTrend = true;
        sar = ep;
        ep = data[i].high;
        currentAf = af;
      } else {
        if (data[i].low < ep) {
          ep = data[i].low;
          currentAf = Math.min(currentAf + af, maxAf);
        }
      }
    }
    
    result.push({ time: data[i].time, value: sar });
  }
  
  return result;
};

// Ichimoku Cloud
const calculateIchimoku = (data, conversionPeriod = 9, basePeriod = 26, spanBPeriod = 52, displacement = 26) => {
  const getHighLow = (startIdx, period) => {
    let high = -Infinity;
    let low = Infinity;
    for (let i = startIdx; i > startIdx - period && i >= 0; i--) {
      high = Math.max(high, data[i].high);
      low = Math.min(low, data[i].low);
    }
    return { high, low };
  };
  
  const conversionLine = [];
  const baseLine = [];
  const spanA = [];
  const spanB = [];
  const laggingSpan = [];
  
  for (let i = 0; i < data.length; i++) {
    // Conversion Line (Tenkan-sen)
    if (i >= conversionPeriod - 1) {
      const hl = getHighLow(i, conversionPeriod);
      conversionLine.push({ time: data[i].time, value: (hl.high + hl.low) / 2 });
    }
    
    // Base Line (Kijun-sen)
    if (i >= basePeriod - 1) {
      const hl = getHighLow(i, basePeriod);
      baseLine.push({ time: data[i].time, value: (hl.high + hl.low) / 2 });
    }
    
    // Leading Span A (Senkou Span A)
    if (i >= basePeriod - 1 && conversionLine.length > 0 && baseLine.length > 0) {
      const convValue = conversionLine[conversionLine.length - 1].value;
      const baseValue = baseLine[baseLine.length - 1].value;
      const futureTime = data[Math.min(i + displacement, data.length - 1)].time;
      spanA.push({ time: futureTime, value: (convValue + baseValue) / 2 });
    }
    
    // Leading Span B (Senkou Span B)
    if (i >= spanBPeriod - 1) {
      const hl = getHighLow(i, spanBPeriod);
      const futureTime = data[Math.min(i + displacement, data.length - 1)].time;
      spanB.push({ time: futureTime, value: (hl.high + hl.low) / 2 });
    }
    
    // Lagging Span (Chikou Span)
    if (i >= displacement) {
      laggingSpan.push({ time: data[i - displacement].time, value: data[i].close });
    }
  }
  
  return { conversionLine, baseLine, spanA, spanB, laggingSpan };
};

// ============= INDICATOR DEFINITIONS =============

const INDICATOR_CATEGORIES = {
  'Overlay': [
    { id: 'SMA20', name: 'SMA (20)', color: '#fbbf24', type: 'overlay' },
    { id: 'SMA50', name: 'SMA (50)', color: '#f97316', type: 'overlay' },
    { id: 'SMA100', name: 'SMA (100)', color: '#06b6d4', type: 'overlay' },
    { id: 'SMA200', name: 'SMA (200)', color: '#a855f7', type: 'overlay' },
    { id: 'EMA12', name: 'EMA (12)', color: '#22c55e', type: 'overlay' },
    { id: 'EMA26', name: 'EMA (26)', color: '#ef4444', type: 'overlay' },
    { id: 'EMA50', name: 'EMA (50)', color: '#3b82f6', type: 'overlay' },
    { id: 'BB', name: 'Bollinger Bands', color: '#8b5cf6', type: 'overlay' },
    { id: 'VWAP', name: 'VWAP', color: '#ec4899', type: 'overlay' },
    { id: 'PSAR', name: 'Parabolic SAR', color: '#14b8a6', type: 'overlay' },
    { id: 'ICHIMOKU', name: 'Ichimoku Cloud', color: '#f59e0b', type: 'overlay' },
  ],
  'Oscillators': [
    { id: 'RSI', name: 'RSI (14)', color: '#a855f7', type: 'oscillator', range: [0, 100] },
    { id: 'STOCH', name: 'Stochastic', color: '#3b82f6', type: 'oscillator', range: [0, 100] },
    { id: 'WILLR', name: 'Williams %R', color: '#ef4444', type: 'oscillator', range: [-100, 0] },
    { id: 'CCI', name: 'CCI (20)', color: '#22c55e', type: 'oscillator', range: [-200, 200] },
  ],
  'Momentum': [
    { id: 'MACD', name: 'MACD', color: '#06b6d4', type: 'momentum' },
  ],
  'Volatility': [
    { id: 'ATR', name: 'ATR (14)', color: '#f97316', type: 'volatility' },
  ],
  'Volume': [
    { id: 'VOL', name: 'Volume', color: '#64748b', type: 'volume' },
  ],
};

import { useVirtualHistory } from '../hooks/useVirtualHistory';
import { useMarketStatus } from '../utils/marketHours';

// Helper to strictly sanitize candle data for lightweight-charts
const sanitizeCandles = (candles) => {
  if (!Array.isArray(candles) || candles.length === 0) return [];
  const valid = [];
  let prevTime = -1;

  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    if (!c) continue;
    let t = c.time;
    if (typeof t === 'string') {
      const parsed = Date.parse(t.includes('T') ? t : `${t}T00:00:00Z`);
      t = !isNaN(parsed) ? Math.floor(parsed / 1000) : null;
    } else if (typeof t === 'number' && t > 1e11) {
      t = Math.floor(t / 1000);
    } else if (typeof t === 'number') {
      t = Math.floor(t);
    } else {
      continue;
    }

    if (!t || isNaN(t)) continue;
    if (t <= prevTime) {
      t = prevTime + 60;
    }
    prevTime = t;

    const o = Number(c.open || c.close || 100);
    const cl = Number(c.close || o);
    const h = Number(c.high || Math.max(o, cl));
    const l = Number(c.low || Math.min(o, cl));

    valid.push({
      time: t,
      open: o,
      high: Math.max(h, o, cl),
      low: Math.min(l, o, cl),
      close: cl,
      volume: Number(c.volume || 1000),
    });
  }
  return valid;
};

const BrokerChart = ({ 
  symbol = 'EUR/USD', 
  instrumentData = null, 
  priceData = null, 
  spread = 0, 
  isVirtual = false,
  height = null,
  compact = false,
  fitContainer = false,
}) => {
  const tx = useTraderText();
  const { locale } = useTraderLanguage();
  const { isDark } = useTraderTheme();
  const marketStatus = useMarketStatus(symbol, instrumentData?.category);

  const [chartType, setChartType] = useState('area'); // 'area' (Line) | 'candles' | 'bars'
  const [timeframe, setTimeframe] = useState('1H');
  const [showIndicatorPanel, setShowIndicatorPanel] = useState(false);
  const [activeIndicators, setActiveIndicators] = useState(['SMA20', 'VOL']);
  const [showClosedOverlay, setShowClosedOverlay] = useState(true);
  const [priceFlash, setPriceFlash] = useState(null); // 'up' | 'down' | null

  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const priceLineRef = useRef(null);
  const indicatorSeriesRef = useRef({});
  const subChartsRef = useRef({});
  const currentSymbolRef = useRef(null);
  const historicalDataRef = useRef([]);
  const lastIndicatorRecalc = useRef(0);
  const prevPriceRef = useRef(0);
  const flashTimeoutRef = useRef(null);
  const fittedKeyRef = useRef('');

  const accountHistory = useAccountPriceHistory(symbol, timeframe, priceData);
  const accountScoped = Boolean(priceData?.account_price_history);
  const virtualHistory = useVirtualHistory(symbol, timeframe, isVirtual && !accountScoped, priceData);
  const isBinance = !isVirtual && isBinancePair(symbol);
  const isLiveHistory = !isVirtual && !accountScoped;
  const binanceHistory = useBinanceHistory(symbol, timeframe, isLiveHistory, priceData);
  const sourceHistory = accountScoped ? accountHistory : isLiveHistory ? binanceHistory : virtualHistory;
  const sourced = accountScoped || isVirtual || (isLiveHistory && sourceHistory.candles.length > 0);

  // Precision calculation
  const isCryptoPair = (symbol || '').toUpperCase().includes('BTC') || (symbol || '').toUpperCase().includes('ETH') || (symbol || '').toUpperCase().includes('SOL');
  const isForexPair = (symbol || '').includes('/') || (symbol || '').length === 6;
  const precision = isCryptoPair ? 2 : isForexPair ? 5 : 2;
  const minMove = isForexPair ? 0.00001 : 0.01;

  // Flatten indicators for lookup
  const allIndicators = useMemo(() => {
    return Object.values(INDICATOR_CATEGORIES).flat();
  }, []);

  // Generate realistic candlestick data anchored right up to the live price
  const generateData = useCallback((basePrice, tf, count = 120) => {
    const data = [];
    const now = Math.floor(Date.now() / 1000);
    const intervalMap = {
      '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
      '1H': 3600, '4H': 14400, '1D': 86400, '1W': 604800,
    };
    const interval = intervalMap[tf] || 3600;
    const volPctMap = {
      '1m': 0.0008, '5m': 0.0012, '15m': 0.0018, '30m': 0.0024,
      '1H': 0.0035, '4H': 0.006, '1D': 0.012, '1W': 0.025,
    };
    const vol = basePrice * (volPctMap[tf] || 0.003);
    const seed = (symbol || 'X').split('').reduce((a, c) => a + c.charCodeAt(0), 0) + tf.charCodeAt(0) * 17;

    // Walk backwards from basePrice so the last bar matches basePrice exactly
    const closes = [basePrice];
    let curr = basePrice;
    for (let i = 1; i < count; i++) {
      const sVal = seed + i * 23;
      const delta = (Math.abs(Math.sin(sVal * 12.9898) * 43758.5453 % 1) - 0.495) * vol;
      curr = Math.max(curr * 0.3, curr - delta);
      closes.push(curr);
    }
    closes.reverse();

    for (let i = 0; i < count; i++) {
      const time = now - (count - 1 - i) * interval;
      const c = closes[i];
      const o = i > 0 ? closes[i - 1] : c * (1 - vol * 0.2);
      const sVal = seed + i * 41;
      const rand1 = Math.abs(Math.sin(sVal * 78.233) * 43758.5453 % 1) * vol * 0.7;
      const rand2 = Math.abs(Math.sin(sVal * 45.17) * 43758.5453 % 1) * vol * 0.7;
      const h = Math.max(o, c) + rand1;
      const l = Math.max(c * 0.01, Math.min(o, c) - rand2);

      data.push({
        time,
        open: Number(o.toFixed(precision)),
        high: Number(h.toFixed(precision)),
        low: Number(l.toFixed(precision)),
        close: Number(c.toFixed(precision)),
        volume: Math.floor(500000 + Math.abs(Math.sin(sVal * 93.7) * 43758.5453 % 1) * 2000000),
      });
    }

    return data;
  }, [symbol, precision]);

  // Update indicators on chart
  const updateIndicators = useCallback((chart, data, indicators) => {
    if (!chart || !data || data.length === 0) return;

    Object.keys(indicatorSeriesRef.current).forEach(key => {
      try { chart.removeSeries(indicatorSeriesRef.current[key]); } catch (e) {}
    });
    indicatorSeriesRef.current = {};

    Object.values(subChartsRef.current).forEach(sub => {
      try { sub.chart.remove(); } catch (e) {}
    });
    subChartsRef.current = {};

    indicators.forEach(indicatorId => {
      const indicator = allIndicators.find(i => i.id === indicatorId);
      if (!indicator) return;

      try {
        if (indicator.type === 'overlay') {
          if (indicatorId.startsWith('SMA')) {
            const period = parseInt(indicatorId.replace('SMA', ''), 10) || 20;
            const smaData = calculateSMA(data, period);
            const series = chart.addLineSeries({ color: indicator.color, lineWidth: 1 });
            series.setData(smaData);
            indicatorSeriesRef.current[indicatorId] = series;
          } else if (indicatorId.startsWith('EMA')) {
            const period = parseInt(indicatorId.replace('EMA', ''), 10) || 12;
            const emaData = calculateEMA(data, period);
            const series = chart.addLineSeries({ color: indicator.color, lineWidth: 1 });
            series.setData(emaData);
            indicatorSeriesRef.current[indicatorId] = series;
          } else if (indicatorId === 'BB') {
            const bb = calculateBollingerBands(data, 20, 2);
            const upper = chart.addLineSeries({ color: indicator.color, lineWidth: 1, lineStyle: 2 });
            const middle = chart.addLineSeries({ color: indicator.color, lineWidth: 1 });
            const lower = chart.addLineSeries({ color: indicator.color, lineWidth: 1, lineStyle: 2 });
            upper.setData(bb.upper);
            middle.setData(bb.middle);
            lower.setData(bb.lower);
            indicatorSeriesRef.current[indicatorId + '_u'] = upper;
            indicatorSeriesRef.current[indicatorId + '_m'] = middle;
            indicatorSeriesRef.current[indicatorId + '_l'] = lower;
          } else if (indicatorId === 'VWAP') {
            const vwap = calculateVWAP(data);
            const series = chart.addLineSeries({ color: indicator.color, lineWidth: 2 });
            series.setData(vwap);
            indicatorSeriesRef.current[indicatorId] = series;
          }
        } else if (indicatorId === 'VOL') {
          const volData = calculateVolume(data);
          const volSeries = chart.addHistogramSeries({
            priceFormat: { type: 'volume' },
            priceScaleId: 'vol',
          });
          volSeries.setData(volData);
          chart.priceScale('vol').applyOptions({
            scaleMargins: { top: 0.85, bottom: 0 },
          });
          indicatorSeriesRef.current[indicatorId] = volSeries;
        }
      } catch (e) {
        console.warn('Indicator error:', indicatorId, e);
      }
    });
  }, [allIndicators]);

  // Initialize or re-create chart whenever symbol, chartType, or theme changes
  useEffect(() => {
    if (!chartContainerRef.current) return;
    const container = chartContainerRef.current;

    // Clean up previous chart
    if (chartRef.current) {
      try { chartRef.current.remove(); } catch (e) {}
      chartRef.current = null;
      seriesRef.current = null;
      priceLineRef.current = null;
      indicatorSeriesRef.current = {};
    }

    const containerHeight = height || container.clientHeight || (compact ? 240 : 420);
    const chartHeight = height || (compact ? 220 : fitContainer ? Math.max(container.clientHeight || 0, 180) : Math.max(containerHeight, 350));

    const chart = createChart(container, {
      localization: { locale: chartLocale() },
      width: Math.max(container.clientWidth || (compact ? 320 : 800), 100),
      height: chartHeight,
      layout: {
        background: { type: 'solid', color: isDark ? '#131722' : '#ffffff' },
        textColor: isDark ? '#d1d4dc' : '#131722',
      },
      grid: {
        vertLines: { color: isDark ? '#1e222d' : '#f0f3fa' },
        horzLines: { color: isDark ? '#1e222d' : '#f0f3fa' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: {
        borderVisible: false,
        autoScale: true,
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: Boolean(marketStatus.isOpen),
      handleScale: Boolean(marketStatus.isOpen),
    });

    let mainSeries;
    const priceFormat = isVirtual 
      ? { type: 'price', precision: 8, minMove: 0.00000001 }
      : { type: 'price', precision: precision, minMove: minMove };

    if (chartType === 'area') {
      mainSeries = chart.addAreaSeries({
        topColor: 'rgba(6, 182, 212, 0.35)',
        bottomColor: 'rgba(6, 182, 212, 0.01)',
        lineColor: '#06b6d4',
        lineWidth: 2,
        priceFormat,
        lastValueVisible: true,
        priceLineVisible: false,
      });
    } else if (chartType === 'bars') {
      mainSeries = chart.addBarSeries({
        upColor: '#22c55e',
        downColor: '#ef4444',
        priceFormat,
        lastValueVisible: true,
        priceLineVisible: false,
      });
    } else {
      mainSeries = chart.addCandlestickSeries({
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderUpColor: '#22c55e',
        borderDownColor: '#ef4444',
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
        priceFormat,
        lastValueVisible: true,
        priceLineVisible: false,
      });
    }

    chartRef.current = chart;
    seriesRef.current = mainSeries;
    currentSymbolRef.current = symbol;

    // Load initial bars
    const basePrice = Number(priceData?.bid || priceData?.price || 100);
    const rawHistory = (sourceHistory.candles && sourceHistory.candles.length >= 10)
      ? sourceHistory.candles
      : generateData(basePrice, timeframe, 120);

    const cleanBars = sanitizeCandles(rawHistory);
    historicalDataRef.current = cleanBars;

    if (chartType === 'area') {
      mainSeries.setData(cleanBars.map(c => ({ time: c.time, value: c.close })));
    } else {
      mainSeries.setData(cleanBars);
    }

    // Attach horizontal live price line across chart
    const initialPrice = Number(priceData?.bid || priceData?.price || (cleanBars.at(-1)?.close || 100));
    prevPriceRef.current = initialPrice;
    try {
      priceLineRef.current = mainSeries.createPriceLine({
        price: initialPrice,
        color: '#06b6d4',
        lineWidth: 1.5,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: 'BID',
      });
    } catch (e) {}

    chart.timeScale().fitContent();
    updateIndicators(chart, cleanBars, activeIndicators);

    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0] && chartRef.current) {
        const { width, height: observedHeight } = entries[0].contentRect;
        const targetHeight = height || (compact ? 220 : fitContainer ? Math.max(observedHeight || 0, 180) : chartHeight);
        chartRef.current.applyOptions({ 
          width: Math.max(width, 100), 
          height: targetHeight 
        });
      }
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
        priceLineRef.current = null;
      }
    };
  }, [symbol, isDark, chartType, timeframe, precision, minMove, height, compact, fitContainer]);

  // Update data when backend history arrives
  useEffect(() => {
    if (!sourced || !seriesRef.current || !sourceHistory.candles || sourceHistory.candles.length === 0) return;
    const cleanBars = sanitizeCandles(sourceHistory.candles);
    if (cleanBars.length === 0) return;

    historicalDataRef.current = cleanBars;
    try {
      if (chartType === 'area') {
        seriesRef.current.setData(cleanBars.map(c => ({ time: c.time, value: c.close })));
      } else {
        seriesRef.current.setData(cleanBars);
      }
      if (chartRef.current) {
        updateIndicators(chartRef.current, cleanBars, activeIndicators);
      }
      const key = `${symbol}:${timeframe}:${chartType}:${isDark}`;
      if (fittedKeyRef.current !== key) {
        chartRef.current?.timeScale().fitContent();
        fittedKeyRef.current = key;
      }
    } catch (e) {
      console.warn('SetData error:', e);
    }
  }, [sourced, sourceHistory.candles, symbol, timeframe, chartType, isDark, activeIndicators, updateIndicators]);

  // Update indicators on change
  useEffect(() => {
    if (!chartRef.current || !historicalDataRef.current.length) return;
    updateIndicators(chartRef.current, historicalDataRef.current, activeIndicators);
  }, [activeIndicators, updateIndicators]);

  // Update interactivity on market hours change
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.applyOptions({
        handleScroll: Boolean(marketStatus.isOpen),
        handleScale: Boolean(marketStatus.isOpen),
      });
    }
  }, [marketStatus.isOpen]);

  // Reset overlay on symbol change
  useEffect(() => {
    setShowClosedOverlay(true);
  }, [symbol]);

  // Real-time tick updater: moves price line & active point on every WebSocket tick
  useEffect(() => {
    // Market hours rule: freeze chart movement if market is closed
    if (!marketStatus.isOpen) return;

    if (!seriesRef.current || !priceData) return;

    const tickPrice = Number(
      priceData.price || 
      priceData.bid || 
      (priceData.bid && priceData.ask ? (Number(priceData.bid) + Number(priceData.ask)) / 2 : 0)
    );
    if (!tickPrice || isNaN(tickPrice) || tickPrice <= 0) return;

    const prev = prevPriceRef.current || tickPrice;
    const isUp = tickPrice >= prev;
    prevPriceRef.current = tickPrice;

    // Flash price badge
    setPriceFlash(isUp ? 'up' : 'down');
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    flashTimeoutRef.current = setTimeout(() => setPriceFlash(null), 800);

    // 1. Move horizontal price line across the canvas
    if (priceLineRef.current) {
      try {
        priceLineRef.current.applyOptions({
          price: tickPrice,
          color: isUp ? '#22c55e' : '#ef4444',
          title: isUp ? '▲ BID' : '▼ BID',
        });
      } catch (e) {}
    }

    // 2. Move active candlestick or line point
    const intervalMap = {
      '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
      '1H': 3600, '4H': 14400, '1D': 86400, '1W': 604800,
    };
    const interval = intervalMap[timeframe] || 3600;
    const nowSec = Math.floor(Date.now() / 1000);
    const bucketTime = Math.floor(nowSec / interval) * interval;

    const candles = historicalDataRef.current;
    if (!candles || candles.length === 0) return;

    const last = candles[candles.length - 1];
    const lastTime = Number(last.time) || bucketTime;
    const candleTime = Math.max(bucketTime, lastTime);

    if (candleTime === lastTime) {
      const updated = {
        ...last,
        time: candleTime,
        high: Math.max(last.high, tickPrice),
        low: Math.min(last.low, tickPrice),
        close: tickPrice,
        volume: (last.volume || 0) + 1,
      };
      candles[candles.length - 1] = updated;

      try {
        if (chartType === 'area') {
          seriesRef.current.update({ time: candleTime, value: tickPrice });
        } else {
          seriesRef.current.update(updated);
        }
      } catch (e) {}
    } else {
      const newCandle = {
        time: candleTime,
        open: last.close || tickPrice,
        high: Math.max(last.close || tickPrice, tickPrice),
        low: Math.min(last.close || tickPrice, tickPrice),
        close: tickPrice,
        volume: 1,
      };
      candles.push(newCandle);
      if (candles.length > 1000) candles.shift();

      try {
        if (chartType === 'area') {
          seriesRef.current.update({ time: candleTime, value: tickPrice });
        } else {
          seriesRef.current.update(newCandle);
        }
      } catch (e) {}
    }

    // Keep live leading edge visible
    try {
      chartRef.current?.timeScale().scrollToRealTime();
    } catch (e) {}

    // Throttle indicator updates to every 2 seconds
    const nowMs = Date.now();
    if (nowMs - lastIndicatorRecalc.current > 2000) {
      lastIndicatorRecalc.current = nowMs;
      updateIndicators(chartRef.current, candles, activeIndicators);
    }
  }, [priceData, marketStatus.isOpen, timeframe, chartType, activeIndicators, updateIndicators]);

  const toggleIndicator = (id) => {
    setActiveIndicators(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const timeframes = ['1m', '5m', '15m', '30m', '1H', '4H', '1D', '1W'];
  const liveBid = priceData?.bid || historicalDataRef.current.at(-1)?.close || 0;
  const liveAsk = priceData?.ask || (liveBid ? liveBid + (spread || 0.0002) : 0);

  return (
    <div className="w-full h-full flex flex-col relative select-none" data-testid="trader-chart-region">
      {isBinance && (
        <BinanceStatus 
          quote={priceData} 
          id="binance-chart-status" 
          loading={sourceHistory.loading} 
          error={sourceHistory.error} 
          rows={sourceHistory.candles?.length || 0} 
        />
      )}
      <AccountPriceNotice quote={priceData} id="account-price-chart-notice" />
      {accountScoped && accountHistory.error && (
        <p role="alert" data-testid="account-price-chart-error" className="text-red-500 text-xs px-2">
          {locale.startsWith('tr') ? 'Hesap fiyat geçmişi alınamadı.' : 'Account price history unavailable.'}
        </p>
      )}

      {/* Top Controls Toolbar */}
      <div className={`flex flex-wrap items-center justify-between gap-1.5 p-1.5 border-b transition-colors ${
        isDark ? 'border-gray-800 bg-gray-900/95' : 'border-gray-200 bg-gray-50'
      }`}>
        {/* Left: Symbol & Status (Desktop only) & Timeframes & Chart Type */}
        <div className="flex flex-wrap items-center gap-1.5">
          {!compact && (
            <div className="flex items-center gap-1 mr-2">
              <span className={`text-sm font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {symbol}
              </span>
              {/* Market Status Pill */}
              {marketStatus.isCrypto ? (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  24/7 Live
                </span>
              ) : marketStatus.isOpen ? (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  Market Open
                </span>
              ) : (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center gap-1">
                  <Lock size={10} />
                  Market Closed
                </span>
              )}
            </div>
          )}

          {/* Chart Type Selector */}
          <div className={`flex items-center rounded-lg p-0.5 border ${
            isDark ? 'bg-gray-800/80 border-gray-700/80' : 'bg-gray-200/80 border-gray-300'
          }`}>
            <button
              type="button"
              onClick={() => setChartType('area')}
              title={tx("Area / Line Chart")}
              data-testid="chart-type-area"
              className={`p-1 rounded text-xs flex items-center gap-1 transition-all ${
                chartType === 'area'
                  ? 'bg-cyan-500 text-white shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-black'
              }`}
            >
              <TrendingUp size={13} />
              <span className="hidden sm:inline text-[11px] font-medium">{tx("Line")}</span>
            </button>
            <button
              type="button"
              onClick={() => setChartType('candles')}
              title={tx("Candlestick Chart")}
              data-testid="chart-type-candles"
              className={`p-1 rounded text-xs flex items-center gap-1 transition-all ${
                chartType === 'candles'
                  ? 'bg-cyan-500 text-white shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-black'
              }`}
            >
              <CandlestickChart size={13} />
              <span className="hidden sm:inline text-[11px] font-medium">{tx("Candles")}</span>
            </button>
            <button
              type="button"
              onClick={() => setChartType('bars')}
              title={tx("Bar Chart")}
              data-testid="chart-type-bars"
              className={`p-1 rounded text-xs flex items-center gap-1 transition-all ${
                chartType === 'bars'
                  ? 'bg-cyan-500 text-white shadow-sm'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-black'
              }`}
            >
              <BarChart3 size={13} />
              <span className="hidden sm:inline text-[11px] font-medium">{tx("Bars")}</span>
            </button>
          </div>

          <div className={`h-4 w-px mx-1 ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />

          {/* Timeframe Buttons */}
          <div className="flex items-center gap-0.5">
            {timeframes.map(tf => (
              <button
                key={tf}
                type="button"
                onClick={() => setTimeframe(tf)}
                data-testid={`timeframe-${tf}`}
                className={`px-2 py-1 text-xs font-semibold rounded transition-all ${
                  timeframe === tf
                    ? 'bg-cyan-500 text-white shadow-sm'
                    : isDark ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-200' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>

          <div className={`h-4 w-px mx-1 ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />

          {/* Indicators Button */}
          <button
            type="button"
            onClick={() => setShowIndicatorPanel(!showIndicatorPanel)}
            data-testid="indicators-btn"
            className={`p-1.5 rounded-lg flex items-center gap-1.5 text-xs font-medium border transition-all ${
              activeIndicators.length > 0
                ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                : isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Activity size={14} />
            <span className="hidden sm:inline">{tx("Indicators")}</span>
            {activeIndicators.length > 0 && (
              <span className="bg-cyan-500 text-white px-1.5 py-0.2 rounded-full text-[10px] font-bold">
                {activeIndicators.length}
              </span>
            )}
          </button>

          {/* Fit View Button */}
          <button
            type="button"
            onClick={() => chartRef.current?.timeScale().fitContent()}
            title={tx("Fit Chart View")}
            data-testid="chart-fit-btn"
            className={`p-1.5 rounded-lg border transition-all ${
              isDark ? 'border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Maximize2 size={13} />
          </button>
        </div>

        {/* Right: Real-time Live Price Ticker (Desktop only) */}
        {!compact && liveBid > 0 && (
          <div className="flex items-center gap-3 text-xs">
            <div className={`px-2.5 py-1 rounded-md border flex items-center gap-1.5 transition-colors duration-300 ${
              priceFlash === 'up'
                ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                : priceFlash === 'down'
                ? 'bg-rose-500/20 border-rose-500/40 text-rose-400'
                : isDark ? 'bg-gray-800/60 border-gray-700/60 text-gray-300' : 'bg-white border-gray-200 text-gray-700'
            }`}>
              <span className="text-[10px] uppercase font-bold text-gray-400">{tx("Bid")}:</span>
              <span className="font-mono font-bold tracking-wider">
                {liveBid.toFixed(precision)}
              </span>
            </div>

            <div className={`px-2.5 py-1 rounded-md border hidden sm:flex items-center gap-1.5 ${
              isDark ? 'bg-gray-800/60 border-gray-700/60 text-gray-300' : 'bg-white border-gray-200 text-gray-700'
            }`}>
              <span className="text-[10px] uppercase font-bold text-gray-400">{tx("Ask")}:</span>
              <span className="font-mono font-bold tracking-wider text-rose-400">
                {liveAsk.toFixed(precision)}
              </span>
            </div>
          </div>
        )}
      </div>

      {isVirtual && (
        <div className="px-2 py-1 text-xs text-cyan-600" data-testid="virtual-chart-status">
          {tx("Simulated · ")}{tx(priceData?.session_status || '—')}
          {virtualHistory.loading ? tx(' · Loading…') : ''}
        </div>
      )}
      {isVirtual && virtualHistory.error && (
        <p role="alert" data-testid="virtual-chart-error" className="text-red-500 text-xs px-2">
          {tx(virtualHistory.error)}
        </p>
      )}

      {/* Main Chart Canvas Viewport */}
      <div className="relative w-full min-w-0" style={{ height: fitContainer ? '100%' : `${height || (compact ? 220 : 380)}px`, minHeight: fitContainer ? 0 : `${height || (compact ? 220 : 380)}px` }}>
        <div
          ref={chartContainerRef}
          data-testid="broker-chart"
          data-candle-count={sourced ? sourceHistory.candles.length : undefined}
          data-last-close={sourced ? sourceHistory.candles.at(-1)?.close : undefined}
          className="w-full h-full"
          style={{ height: fitContainer ? '100%' : `${height || (compact ? 220 : 380)}px`, minHeight: fitContainer ? 0 : `${height || (compact ? 220 : 380)}px` }}
        />

        {/* Closed Market Full Overlay */}
        {!marketStatus.isOpen && showClosedOverlay && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-gray-950/75 backdrop-blur-sm p-4"
            data-testid="market-closed-overlay"
          >
            <div className={`max-w-md w-full p-6 rounded-xl border shadow-2xl text-center flex flex-col items-center ${
              isDark ? 'bg-gray-900/95 border-gray-700 text-white' : 'bg-white/95 border-gray-200 text-gray-900'
            }`}>
              <div className="w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500 mb-3 shadow-inner">
                <Lock size={22} />
              </div>
              <h3 className="text-base font-bold mb-1">{tx("Market is Closed")}</h3>
              <p className="text-xs text-rose-400 font-medium mb-3">{marketStatus.reason}</p>

              <div className={`w-full py-2.5 px-3 rounded-lg text-xs mb-3 flex items-center justify-between ${
                isDark ? 'bg-gray-800/80 border border-gray-700/60' : 'bg-gray-100 border border-gray-200'
              }`}>
                <span className="text-gray-400 flex items-center gap-1.5">
                  <Clock size={14} className="text-cyan-400" />
                  <span>{marketStatus.marketName || tx("Operating Hours")}</span>
                </span>
                <span className="font-mono font-semibold text-cyan-400">{marketStatus.workingHours}</span>
              </div>

              <p className="text-[11px] text-gray-400 mb-4 leading-relaxed">
                {tx("Price ticks, chart rendering, and trading are locked outside regular exchange hours. Spot Crypto pairs trade 24/7/365.")}
              </p>

              <button
                type="button"
                onClick={() => setShowClosedOverlay(false)}
                className="text-xs px-3.5 py-1.5 rounded-lg border border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white transition-colors"
              >
                {tx("Inspect Static Chart")}
              </button>
            </div>
          </div>
        )}

        {/* Small badge when overlay is dismissed but market is closed */}
        {!marketStatus.isOpen && !showClosedOverlay && (
          <div className="absolute top-2 right-2 z-10 flex items-center gap-2 px-2.5 py-1 rounded-md bg-rose-950/85 border border-rose-700/60 text-rose-300 text-xs backdrop-blur-md shadow-lg">
            <Lock size={12} className="text-rose-400" />
            <span className="font-medium">{tx("Market Closed · Static View")}</span>
            <button
              type="button"
              onClick={() => setShowClosedOverlay(true)}
              className="text-[11px] text-rose-300 hover:text-white underline ml-1"
            >
              {tx("Details")}
            </button>
          </div>
        )}
      </div>

      {/* Enhanced Indicator Drawer Panel */}
      {showIndicatorPanel && (
        <div
          className={`absolute top-12 left-2 z-50 w-72 max-h-[70vh] overflow-y-auto rounded-lg shadow-2xl border ${
            isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <div className={`sticky top-0 px-3 py-2 border-b font-medium text-sm flex items-center justify-between ${
            isDark ? 'border-gray-800 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-900'
          }`}>
            <div className="flex items-center gap-2">
              <Layers size={16} className="text-cyan-400" />
              <span>{tx("Technical Indicators")}</span>
            </div>
            <button type="button" onClick={() => setShowIndicatorPanel(false)} className="p-1 hover:bg-gray-700/50 rounded">
              <X size={14} />
            </button>
          </div>

          {Object.entries(INDICATOR_CATEGORIES).map(([category, indicators]) => (
            <div key={category} className={`border-b last:border-b-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className={`px-3 py-2 text-xs font-semibold flex items-center justify-between ${
                isDark ? 'text-gray-400 bg-gray-950/40' : 'text-gray-500 bg-gray-50'
              }`}>
                <span>{tx(category)}</span>
              </div>
              <div className="p-1">
                {indicators.map(indicator => (
                  <button
                    key={indicator.id}
                    type="button"
                    onClick={() => toggleIndicator(indicator.id)}
                    data-testid={`indicator-${indicator.id}`}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors ${
                      activeIndicators.includes(indicator.id)
                        ? isDark ? 'bg-cyan-500/20 text-cyan-400 font-medium' : 'bg-cyan-50 text-cyan-600 font-medium'
                        : isDark ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-1 rounded" style={{ backgroundColor: indicator.color }} />
                      <span>{tx(indicator.name)}</span>
                    </div>
                    {activeIndicators.includes(indicator.id) && (
                      <div className="w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Quick Clear Action */}
          <div className={`sticky bottom-0 p-2 border-t ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}>
            <button
              type="button"
              onClick={() => setActiveIndicators([])}
              className={`w-full py-1.5 text-xs font-medium rounded transition-colors ${
                isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tx("Clear All Indicators")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(BrokerChart);
